import { useState, useEffect } from 'react';
import './App.css';
import {
  hasSession,
  getStoredUsername,
  getStoredSessionId,
  loginWithGoogle,
  handleOAuthCallback,
  getUserInfo,
  clearSession
} from './services/auth';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.flydev.silliestduck.com';

type ServerStatus = 'checking' | 'online' | 'offline';

function App() {
  const [status, setStatus] = useState<ServerStatus>('checking');
  const [message, setMessage] = useState<{ text: string; type: 'info' | 'success' | 'error' } | null>(null);

  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState<string | null>(null);
  const [inviteCode, setInviteCode] = useState<string | null>(null);

  const checkServerStatus = async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(`${API_URL}/health`, {
        method: 'GET',
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        setStatus('online');
        return true;
      } else {
        setStatus('offline');
        return false;
      }
    } catch (error) {
      setStatus('offline');
      return false;
    }
  };

  const wakeServer = async () => {
    setMessage({ text: 'Sending wake-up request to server...', type: 'info' });

    try {
      // First request wakes up the server
      await fetch(`${API_URL}/health`, { method: 'GET' });

      // Poll for server to come online
      let attempts = 0;
      const maxAttempts = 30;

      const pollInterval = setInterval(async () => {
        attempts++;

        if (attempts > maxAttempts) {
          clearInterval(pollInterval);
          setMessage({ text: 'Server took too long to wake up. Please try again.', type: 'error' });
          return;
        }

        const isOnline = await checkServerStatus();

        if (isOnline) {
          clearInterval(pollInterval);
          setMessage({ text: 'Server is now online!', type: 'success' });
        }
      }, 1000);
    } catch (error) {
      setMessage({ text: `Failed to wake up server: ${error}`, type: 'error' });
    }
  };

  const handleLogin = () => {
    loginWithGoogle(inviteCode || undefined);
  };

  const handleLogout = () => {
    clearSession();
    setIsAuthenticated(false);
    setUsername(null);
    setMessage({ text: 'Logged out successfully', type: 'info' });
  };

  // Check for invite code in URL hash
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.startsWith('#invite=')) {
      const code = hash.substring(8); // Remove '#invite='
      setInviteCode(code);
      setMessage({ text: 'Ready to accept invite! Click "Login with Google" to continue.', type: 'info' });
    }
  }, []);

  // Check for OAuth callback on mount
  useEffect(() => {
    const { sessionId, error } = handleOAuthCallback();

    if (sessionId) {
      // Fetch user info
      getUserInfo(sessionId).then(({ username, error }) => {
        if (username) {
          setIsAuthenticated(true);
          setUsername(username);
          setMessage({ text: 'Login successful!', type: 'success' });
          // Clean up URL
          window.history.replaceState({}, document.title, window.location.pathname);
        } else {
          setMessage({ text: error || 'Failed to get user info', type: 'error' });
        }
      });
    } else if (error) {
      // Map error codes to user-friendly messages
      const errorMessages: Record<string, string> = {
        'invite_required': 'You need an invite code to sign up. Contact an administrator for an invite.',
        'invalid_invite': 'Invalid invite code. Please check the invite link and try again.',
        'invite_used': 'This invite code has already been used.',
        'invite_expired': 'This invite code has expired. Contact an administrator for a new invite.',
        'auth_failed': 'Authentication failed. Please try again.'
      };

      const friendlyMessage = errorMessages[error] || `Login failed: ${error}`;
      setMessage({ text: friendlyMessage, type: 'error' });
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Check for existing session on mount
  useEffect(() => {
    if (hasSession() && !isAuthenticated) {
      const sessionId = getStoredSessionId();
      const storedUsername = getStoredUsername();

      if (sessionId) {
        // Verify session is still valid
        getUserInfo(sessionId).then(({ username }) => {
          if (username) {
            setIsAuthenticated(true);
            setUsername(username);
          } else {
            // Session invalid, clear it
            clearSession();
          }
        });
      } else if (storedUsername) {
        // Have username but no session
        setUsername(storedUsername);
      }
    }
  }, []);

  useEffect(() => {
    checkServerStatus();

    // Check status every 30 seconds
    const interval = setInterval(checkServerStatus, 30000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  return (
    <div className="container">
      <div className="card">
        <h1>DuckFlight Dev Server</h1>
        <p className="subtitle">Cloud development environment powered by Claude</p>

        <div className="status">
          <div className={`status-indicator ${status}`}></div>
          <span>
            {status === 'checking' && 'Checking server status...'}
            {status === 'online' && 'Server is online'}
            {status === 'offline' && 'Server is sleeping (scaled to zero)'}
          </span>
        </div>

        <div className="actions">
          <button
            className="btn btn-primary"
            onClick={wakeServer}
            disabled={status === 'online' || status === 'checking'}
          >
            Wake Up Server
          </button>

          {!isAuthenticated ? (
            <button
              className="btn btn-secondary"
              onClick={handleLogin}
              disabled={status !== 'online'}
            >
              {inviteCode ? 'Accept Invite with Google' : 'Login with Google'}
            </button>
          ) : (
            <button
              className="btn btn-danger"
              onClick={handleLogout}
            >
              Logout
            </button>
          )}
        </div>

        {isAuthenticated && (
          <div className="auth-status">
            <div className="auth-info">
              ✓ Logged in as <strong>{username}</strong>
            </div>
          </div>
        )}

        {message && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        <div className="info">
          <h3>What is this?</h3>
          <p>
            This is your personal cloud development server that runs Claude Code.
            The server scales to zero when not in use to save costs.
          </p>
          <ul>
            <li>Click "Wake Up Server" if the server is asleep</li>
            <li>Use Google OAuth for secure authentication</li>
            <li>Access your development environment from anywhere</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default App;
